// Partner product review — platform staff review/approve partner products (loans,
// insurance, …) that partners submit. The review QUEUE is read from the CANONICAL catalog
// (auth.products, via listCatalog); the approve/request-changes ACTIONS still drive the
// credit-service lifecycle. See the generic Partner Product model + the identity migration.
import axios from 'axios';
import { listCatalog, type CatalogProduct } from './tenantService';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);

export interface PartnerProduct {
  id: string;
  partner_id?: string;
  code: string;
  name: string;
  funding_model: string;
  product_status: string;
  currency: string;
  interest_method: string;
  default_annual_rate: number;
  max_annual_rate: number;
  min_amount: number;
  max_amount: number;
  max_term_months: number;
  // Generic Partner Product fields. category = CREDIT | INSURANCE | HEALTH | …;
  // fulfillment_domain = which service executes it (credit | benefits); config carries
  // the category-specific terms (e.g. insurance: plan_type, employee_premium, coverage).
  category?: string;
  fulfillment_domain?: string;
  fulfillment_reference?: string;
  config?: Record<string, unknown>;
  submitted_by?: string;
  submitted_at?: string;
  reviewed_by?: string;
  review_note?: string;
  scheduled_at?: string;
  updated_at: string;
}

// Map a canonical catalog row (auth.products) to the review-page shape. Credit-execution
// terms (rate/amount/term) are carried in the canonical config summary; insurance terms live
// in config too — so the review page renders from canonical alone, no credit.products read.
function fromCatalog(c: CatalogProduct): PartnerProduct {
  const m = (c.metadata ?? {}) as Record<string, any>;
  const cfg = (m.config ?? {}) as Record<string, any>;
  const updatedAt = (c as any).updatedAt as string | undefined;
  return {
    id: m.creditProductId ?? c.code.replace(/^partner:/, ''),
    partner_id: m.partnerId,
    code: c.code,
    name: c.name,
    funding_model: 'partner_funded',
    product_status: 'partner_review',
    currency: cfg.currency ?? 'TZS',
    interest_method: cfg.interest_method ?? 'flat',
    default_annual_rate: Number(cfg.default_annual_rate ?? 0),
    max_annual_rate: Number(cfg.max_annual_rate ?? 0),
    min_amount: Number(cfg.min_amount ?? 0),
    max_amount: Number(cfg.max_amount ?? 0),
    max_term_months: Number(cfg.max_term_months ?? 0),
    category: m.partnerCategory ?? 'CREDIT',
    fulfillment_domain: m.fulfillmentDomain ?? 'credit',
    config: m.config,
    submitted_at: updatedAt,
    updated_at: updatedAt ?? '',
  };
}

// The review queue: partner products awaiting a platform decision, read from the CANONICAL
// catalog (partner products whose credit lifecycle status is partner_review). Falls back to
// the credit endpoint if the canonical catalog is unreachable, so review never goes dark.
export const getPendingProducts = async (): Promise<PartnerProduct[]> => {
  try {
    const catalog = await listCatalog();
    return catalog
      .filter(
        (c) =>
          c.productType === 'PARTNER_PRODUCT' &&
          ((c.metadata as any)?.creditStatus ?? '') === 'partner_review',
      )
      .map(fromCatalog);
  } catch {
    const r = await axios.get<{ products: PartnerProduct[] }>(`${API_V1}/credit/admin/products`, {
      params: { status: 'partner_review' },
    });
    return r.data.products ?? [];
  }
};

// Review decision. The backend enforces four-eyes (reviewer ≠ submitter/creator) + valid
// transitions. Outcomes out of partner_review: approve → active (published now); schedule →
// scheduled (goes live at scheduled_at, a background sweep publishes it); request-changes →
// changes_requested (a labelled state that returns the working copy to the partner with a note).
const setStatus = (id: string, product_status: string, review_note?: string, scheduled_at?: string) =>
  axios.post(`${API_V1}/credit/admin/products/${id}/status`, { product_status, review_note, scheduled_at }).then((r) => r.data);

export const approveProduct = (id: string) => setStatus(id, 'active');
export const requestChanges = (id: string, note: string) => setStatus(id, 'changes_requested', note);
export const scheduleProduct = (id: string, scheduledAt: string) => setStatus(id, 'scheduled', undefined, scheduledAt);
export const suspendProduct = (id: string) => setStatus(id, 'suspended');
